import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { adminFetch } from './useAdminApi';

/**
 * Admin-side reads + review actions for orders.account_change_requests.
 *
 * Reads go direct via PostgREST under the superadmin SELECT RLS policy (added
 * in 20260615000001_account_change_requests.sql), scoped to one customer.
 * The apply/reject write goes through the service-role
 * admin-review-account-change function.
 */

export type AccountChangeKind = 'address' | 'contact';
export type AccountChangeStatus = 'pending' | 'applied' | 'rejected';

export interface AdminAccountChangeRequest {
  id: string;
  customerId: string;
  locationId: string | null;
  kind: AccountChangeKind;
  requestedByEmail: string | null;
  currentValues: Record<string, string | null> | null;
  proposedValues: Record<string, string | null>;
  note: string | null;
  status: AccountChangeStatus;
  reviewNote: string | null;
  reviewedByEmail: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

/** All change requests for one customer (pending first, then most-recent). */
export function useAdminAccountChangeRequests(customerId: string | null | undefined) {
  return useQuery<AdminAccountChangeRequest[]>({
    queryKey: ['admin', 'account-change-requests', customerId],
    enabled: !!customerId,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('account_change_requests')
        .select(
          'id,customer_id,location_id,kind,requested_by_email,current_values,proposed_values,' +
            'note,status,review_note,reviewed_by_email,reviewed_at,created_at',
        )
        .eq('customer_id', customerId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const raw = (data ?? []) as unknown as Array<Record<string, unknown>>;
      const rows = raw.map((row) => ({
        id: row.id as string,
        customerId: row.customer_id as string,
        locationId: (row.location_id as string | null) ?? null,
        kind: row.kind as AccountChangeKind,
        requestedByEmail: (row.requested_by_email as string | null) ?? null,
        currentValues: (row.current_values as Record<string, string | null> | null) ?? null,
        proposedValues: (row.proposed_values as Record<string, string | null>) ?? {},
        note: (row.note as string | null) ?? null,
        status: row.status as AccountChangeStatus,
        reviewNote: (row.review_note as string | null) ?? null,
        reviewedByEmail: (row.reviewed_by_email as string | null) ?? null,
        reviewedAt: (row.reviewed_at as string | null) ?? null,
        createdAt: row.created_at as string,
      }));
      // Pending first; within a group, most recent first.
      return rows.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (b.status === 'pending' && a.status !== 'pending') return 1;
        return b.createdAt.localeCompare(a.createdAt);
      });
    },
  });
}

interface ReviewInput {
  requestId: string;
  action: 'apply' | 'reject';
  review_note?: string;
  /** Customer id, used to invalidate the right per-customer queries. */
  customerId: string;
}

export function useReviewAccountChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewInput) =>
      adminFetch<{ ok: true; requestId: string; status: string }>(
        'admin-review-account-change',
        {
          method: 'POST',
          body: JSON.stringify({
            requestId: input.requestId,
            action: input.action,
            review_note: input.review_note,
          }),
        },
      ),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ['admin', 'account-change-requests', input.customerId] });
      // Applied change wrote into the customer/location row — refresh the
      // detail loader so the updated address/contact shows.
      qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', input.customerId] });
    },
  });
}
